import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';;
import { useSnackbar } from '../Stateless/SnackBarProvider';
import { useDriverManagerApi } from '../../hooks/useDriverManagerApi';

type Params = {
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
  addedByFilter?: string;
  statusFilter?: string;
  search?: string;
};


export const useAllDriverManagement = (queryParams: Params, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {

  const { getAllDriverManager } = useDriverManagerApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['getDriver', queryParams],
    queryFn: () => {
      return getAllDriverManager(queryParams);
    },
    ...options,
  });

  const isLoading = isFetching || isRefetching;

  return {
    data,
    isLoading,
    refetch
  };
};

export const UseCreateDriverManager = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { createDriverManager } = useDriverManagerApi();
  return useMutation({
    mutationKey: ['CreateDriver'],
    mutationFn: (data: any) => createDriverManager(data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Driver created successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getDriver'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to create Driver',
        'error'
      );
    },
  });
};

export const usePatchDriverManagement = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { getToggleStatus } = useDriverManagerApi();

  return useMutation({
    mutationKey: ['PatchDriver'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      getToggleStatus({ id, status: data.status }),
    onSuccess: (_response, variables) => {
      const action =
        variables.data.status === 'unblocked' ? 'unblocked' : 'blocked';
      showMessage(`Driver ${action} successfully`, 'success');
      queryClient.invalidateQueries({
        queryKey: ['getDriver'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type || 'Failed to block the Driver',
        'error'
      );
    },
  });
};

export const useDeleteDriverManagement = () => {
  const queryClient = useQueryClient();
  const { deleteDriver } = useDriverManagerApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['DeleteDriver'],
    mutationFn: (ids: string[]) => {
      return deleteDriver(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Driver deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getDriver'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to deleted Driver',
        'error'
      );
    },
  });
};

export const useEditDriverManager = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editDriver } = useDriverManagerApi();

  return useMutation({
    mutationKey: ['EditDriver'],
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editDriver(id, data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Driver Updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getDriver'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update the Driver',
        'error'
      );
    },
  });
};
